import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, CalendarCheck, TrendingUp } from "lucide-react";
import { useLenis } from "lenis/react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761062083136-dcfbd2a000f9?q=85&w=1400&auto=format&fit=crop";

function MaskedLine({ children, delay }) {
  return (
    <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const STATS = [
  { value: "−40%", label: "Offerta lancio su tutti i piani, fino a fine anno" },
  { value: "0%", label: "Commissioni sulle prenotazioni del tuo sito" },
  { value: "100%", label: "Design sartoriale, mai template" },
];

export default function Hero() {
  const cardRef = useRef(null);
  const lenis = useLenis();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

  const { scrollYProgress } = useScroll({ target: cardRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const onMouseMove = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const go = (id) => (e) => {
    e.preventDefault();
    lenis?.scrollTo(id, { offset: -84, duration: 1.5 });
  };

  return (
    <section className="relative overflow-hidden pb-20 pt-32 md:pt-40 lg:pb-28">
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full opacity-[0.13]"
        style={{ background: "radial-gradient(circle, #E05A47 0%, transparent 65%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[480px] w-[480px] rounded-full opacity-[0.09]"
        style={{ background: "radial-gradient(circle, #D4A359 0%, transparent 65%)" }}
        aria-hidden="true"
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-3 font-accent text-[11px] font-medium uppercase tracking-[0.32em] text-terracotta"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terracotta opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-terracotta" />
            </span>
            Web design per attività locali
          </motion.p>

          <h1
            data-testid="hero-title"
            className="mt-6 font-serif text-[clamp(2.35rem,5.2vw,4.6rem)] font-medium leading-[1.06] tracking-tight text-bone"
          >
            <MaskedLine delay={0.2}>Trasformiamo</MaskedLine>
            <MaskedLine delay={0.32}>il tuo locale nella</MaskedLine>
            <MaskedLine delay={0.44}>
              <em className="italic text-terracotta">vetrina</em> digitale
            </MaskedLine>
            <MaskedLine delay={0.56}>della città.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-stone sm:text-lg"
          >
            Design sartoriale per ristoranti, hotel, B&B e botteghe che vogliono
            essere scelti. Più prenotazioni dirette, zero commissioni di
            intermediazione.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contatti"
              onClick={go("#contatti")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2.5 rounded-full bg-terracotta px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-all duration-300 hover:bg-[#f0695a]"
            >
              Inizia il progetto
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
            <a
              href="#casi-studio"
              onClick={go("#casi-studio")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2.5 rounded-full border border-line-strong px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:border-bone/50"
            >
              Guarda i progetti
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.92, ease: EASE }}
            className="mt-14 grid grid-cols-1 gap-6 border-t border-line pt-8 sm:grid-cols-3"
          >
            {STATS.map((s, i) => (
              <div key={s.value} data-testid={`hero-stat-${i + 1}`} className={i > 0 ? "sm:border-l sm:border-line sm:pl-6" : ""}>
                <p className="font-serif text-3xl tracking-tight text-bone">{s.value}</p>
                <p className="mt-1.5 text-[13px] leading-snug text-faded">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="lg:col-span-5"
          style={{ perspective: 1000 }}
        >
          <motion.div
            ref={cardRef}
            data-testid="hero-image-card"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div className="overflow-hidden rounded-b-3xl rounded-t-[9rem] border border-line bg-panel">
              <motion.img
                src={HERO_IMG}
                alt="Sala di un ristorante italiano con luce calda"
                style={{ y: imgY }}
                className="h-[380px] w-full scale-[1.12] object-cover sm:h-[460px] lg:h-[540px]"
                loading="eager"
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(14,12,11,0.72) 0%, transparent 42%)",
                }}
                aria-hidden="true"
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
              style={{ transform: "translateZ(60px)" }}
              className="glass float-soft absolute -bottom-7 left-4 flex items-center gap-4 rounded-2xl px-5 py-4 sm:left-8"
            >
              <TrendingUp size={22} className="text-terracotta" />
              <div>
                <p className="font-serif text-2xl leading-none tracking-tight text-bone">0%</p>
                <p className="mt-1 font-accent text-[9px] uppercase tracking-[0.24em] text-stone">
                  commissioni sulle prenotazioni
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
              style={{ transform: "translateZ(40px)" }}
              className="glass absolute -right-3 top-8 flex items-center gap-2.5 rounded-full px-4 py-2.5 sm:-right-6"
              data-testid="hero-badge-direct"
            >
              <CalendarCheck size={15} className="text-ochre" />
              <span className="font-accent text-[10px] font-semibold uppercase tracking-[0.2em] text-bone">
                Booking direct
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}